import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { connect } from "react-redux";
import { CircularProgress, Container, Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { LocaleContext } from "../../routing/LangRouter";

//import redux storage
import { saveTourList } from "../../store/actions";

//import constants
import BASE_URL from "../../constants/baseUrls";
import { IS_MOBILE } from "../../constants/breakpoints";

//import general components
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import Loading from "../../components/common/loading/loading";
import BaseTitle from "../../components/common/base-title/base-title";
import BaseSort from "../../components/common/base-sort/base-sort";
import Footer from "../../components/common/footer/footer";

//import view components
import TourSearchForm from "../../components/view/tour/tour-search-form/tour-search-form";
import TourList from "../../components/view/tour/tour-list/tour-list";
import TourSkeleton from "../../components/view/tour/tour-skeleton/tour-skeleton";
import TourFilter from "../../components/view/tour/tour-filter/tour-filter";

//import images and icons
import { Edit2, Filter, Sort } from "iconsax-react";

//import styles
import "./tour.scss";
import FilterSortActions from "../../components/common/filter-sort-actions/filter-sort-actions";

function Tour({ dispatch }) {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const [parent] = useAutoAnimate();
    const [tourData, setTourData] = useState([]);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    //update search fields state
    const [formData, setFormData] = useState(null);
    const handleFormChange = data => {
        setFormData(data);
    };

    //list of sorting options
    const sortOptions = [
        { value: "1", label: `${t("earliest")}`, checked: true },
        { value: "2", label: `${t("lastest")}`, checked: false },
        { value: "3", label: `${t("expensive")}`, checked: false },
        { value: "4", label: `${t("cheapest")}`, checked: false },
    ];

    // handle sort change
    const [selectedSortValue, setSelectedSortValue] = useState(tourData?.params?.sort || "1");
    const handleSortChange = value => {
        setSelectedSortValue(value);
    };

    //call request filters
    const [filterList, setFilterList] = useState();
    const getFilterList = async () => {
        const response = await axios.get(`${BASE_URL}/api/v2/itinerary/tour/filters/`);
        setFilterList(response?.data?.data);
    };
    useEffect(() => {
        getFilterList();
    }, []);

    const [appliedFilters, setAppliedFilters] = useState();

    //call request tour list
    const getTourList = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/itinerary/tour/list/`, {
                params: {
                    sort: selectedSortValue,
                    search: formData?.searchValue,
                    begin_date_from: formData?.date?.startDate,
                    begin_date_to: formData?.date?.endDate,
                    origin_countries: appliedFilters?.origin_countries,
                    destination_countries: appliedFilters?.destination_countries,
                    origin_cities: appliedFilters?.origin_cities,
                    destination_cities: appliedFilters?.destination_cities,
                    accommodations: appliedFilters?.accommodations,
                    origin_airports: appliedFilters?.origin_airports,
                    destination_airports: appliedFilters?.destination_airports,
                },
            });
            dispatch(saveTourList(response.data?.data));
            setTourData(response.data);
        } catch (error) {
            dispatch(saveTourList(null));
            setTourData([]);
            toast.error(t("errorMessage"));
        } finally {
            setIsLoading(false);
            setIsFilterOpen(false);
        }
    };
    useEffect(() => {
        getTourList();
    }, [formData, selectedSortValue, appliedFilters]);

    //state to manage the visibility of the edit form
    const [isEditOpen, setIsEditOpen] = useState(IS_MOBILE ? false : true);

    //handle navigate to home
    const navigateToHome = () => {
        navigate(`/${locale}`, { replace: true });
    };

    return (
        <Grid className="tour" container item xs={12} mb={5}>
            {isLoading && <Loading />}

            <MobileHeader
                title={t("tour")}
                edit={<Edit2 size="20" color="var(--primary-color)" onClick={() => setIsEditOpen(!isEditOpen)} />}
                handleClick={navigateToHome}
            />

            <Grid
                className="tour__background"
                container
                item
                xs={12}
                rowGap={2}
                alignItems={{ xs: "center", md: "flex-end" }}
                ref={parent}
                sx={{ height: "fit-content" }}
            >
                <Grid container item xs={12} direction="column" justifyContent="center" alignItems="center">
                    <h1 className="tour__title">{t("tourTitle")}</h1>
                    <span className="tour__sub-title">{t("tourTitle2")}</span>
                </Grid>
                {isEditOpen && <TourSearchForm handleFormChange={handleFormChange} filterList={filterList} />}
            </Grid>

            {isLoading ? (
                <TourSkeleton />
            ) : (
                <Container maxWidth="xl">
                    <Grid container item xs={12} rowGap={{ xs: 2, md: 5 }}>
                        {IS_MOBILE ? <FilterSortActions onFilterClick={() => setIsFilterOpen(true)} onSortClick={() => setIsSortOpen(true)} /> : null}

                        <Grid container item xs={12} justifyContent="space-between">
                            <BaseTitle title={t("tourList")} />
                        </Grid>

                        <Grid container item xs={12} justifyContent="space-between">
                            <BaseSort
                                open={isSortOpen}
                                setOpen={setIsSortOpen}
                                optionList={sortOptions}
                                handleChange={handleSortChange}
                                defaultValue={tourData?.params?.sort}
                            />
                        </Grid>

                        <Grid container item xs={12} justifyContent="space-between" alignItems="flex-start">
                            <Grid container item xs={2} sx={{ position: "sticky !important", top: "5rem", zIndex: 10 }}>
                                <TourFilter
                                    open={isFilterOpen}
                                    setOpen={setIsFilterOpen}
                                    defaultValue={tourData.params}
                                    filterList={filterList}
                                    setAppliedFilters={setAppliedFilters}
                                />
                            </Grid>
                            <Grid container item md={9.8}>
                                <TourList />
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            )}
            <Footer />
        </Grid>
    );
}

export default connect()(Tour);
